import {
  AlertTriangle,
  CheckCircle2,
  CircleHelp,
  Clock,
  RefreshCw,
  Scale,
  Send,
  XCircle,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, SelectField } from '@krp/ui-kit'

import '../../../shared/uc1b/journals.css'
import { QueryState } from '../../../shared/uc1b/QueryState'
import type { DeliveryActions, DeliveryVm, OperationFilter, StatusCountVm } from '../model/types'

const STATUS_ICON: Readonly<Record<string, ReactNode>> = {
  Pending: <Clock size={16} aria-hidden="true" />,
  Sent: <Send size={16} aria-hidden="true" />,
  Unknown: <CircleHelp size={16} aria-hidden="true" />,
  Failed: <XCircle size={16} aria-hidden="true" />,
  Succeeded: <CheckCircle2 size={16} aria-hidden="true" />,
}

const FILTER_OPTIONS: readonly { value: OperationFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'active', label: 'В пути' },
  { value: 'unknown', label: 'Исход неизвестен' },
  { value: 'failed', label: 'Не доставлены' },
  { value: 'succeeded', label: 'Доставлены' },
]

function Counts({ items, label }: { items: readonly StatusCountVm[]; label: string }) {
  return (
    <dl className="aqsi-journal__facts" aria-label={label}>
      {items.map((item) => (
        <div key={item.key} style={{ display: 'contents' }}>
          <dt>{item.label}</dt>
          <dd>
            {item.attention && <AlertTriangle size={14} aria-hidden="true" />} {item.value}
            {item.attention ? ' — требует внимания' : ''}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * Доставка каталога в кабинет AQSI: счётчики, операции, сверка привязок.
 * «Исход неизвестен» и «Не доставлена» отличаются значком и словами, не цветом;
 * текст отказа кабинета показывается дословно (ADR-8).
 */
export function DeliveryScreen({ vm, actions }: { vm: DeliveryVm; actions: DeliveryActions }) {
  const operations = vm.operations
  const reconciliation = vm.reconciliation
  return (
    <section className="aqsi-journal" aria-labelledby="aqsi-delivery-title">
      <header className="aqsi-journal__head">
        <h2 id="aqsi-delivery-title">Доставка каталога</h2>
        <Button
          variant="ghost"
          icon={<RefreshCw size={16} aria-hidden="true" />}
          busy={vm.status.query.refreshing || operations.query.refreshing}
          onClick={actions.reload}
        >
          Обновить
        </Button>
      </header>

      <QueryState query={vm.status.query} emptyText="Доставки ещё не было" onRetry={actions.reload} />
      {vm.status.query.status === 'ready' && (
        <>
          {vm.status.pause !== null && (
            <p className="aqsi-journal__note" data-tone="warning" role="status">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>{vm.status.pause}</span>
            </p>
          )}
          <Counts items={vm.status.counts} label="Счётчики доставки" />
          <p className="aqsi-journal__note">
            Контрольная точка: {vm.status.checkpointText}. {vm.status.bindingsText}
          </p>
        </>
      )}

      <h3>Операции</h3>
      <div className="aqsi-journal__filters">
        <SelectField
          label="Показывать"
          value={operations.filter}
          options={FILTER_OPTIONS}
          onChange={(value) => actions.filterChanged(value as OperationFilter)}
        />
      </div>
      <QueryState query={operations.query} emptyText="Операций доставки нет" onRetry={actions.reload} />
      {operations.query.status === 'ready' && (
        <>
          {operations.truncated && (
            <p className="aqsi-journal__note" data-tone="warning" role="status">
              <AlertTriangle size={16} aria-hidden="true" />
              <span>Показаны не все операции: сервер отдаёт ограниченный список, незавершённые первыми.</span>
            </p>
          )}
          {operations.rows.length === 0 ? (
            <p className="aqsi-journal__note">Под этот срез операций нет.</p>
          ) : (
            <ul className="aqsi-journal__list" aria-label="Операции доставки">
              {operations.rows.map((row) => (
                <li key={row.id} className="aqsi-journal__item" data-attention={row.tone === 'attention'}>
                  <div className="aqsi-journal__item-main">
                    {STATUS_ICON[row.status] ?? <AlertTriangle size={16} aria-hidden="true" />}
                    <strong>{row.statusText}</strong>
                    <span>{row.title}</span>
                  </div>
                  <div className="aqsi-journal__meta">
                    <span>{row.step}</span>
                    <span>{row.timing}</span>
                    {row.nextAttempt !== null && <span>{row.nextAttempt}</span>}
                    {row.error !== null && <span role="status">Причина: {row.error}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <h3>Сверка привязок</h3>
      <div className="aqsi-journal__actions">
        <Button
          icon={<Scale size={16} aria-hidden="true" />}
          busy={reconciliation.busy}
          onClick={actions.reconcile}
        >
          {reconciliation.busy ? 'Сверка идёт…' : 'Сверить с каталогом кабинета'}
        </Button>
      </div>
      {reconciliation.failure !== null && (
        <p role="alert" className="aqsi-journal__note" data-tone="warning">
          Сверка не выполнена: {reconciliation.failure.message} (код {reconciliation.failure.code}
          {reconciliation.failure.correlationId !== null ? `, запрос ${reconciliation.failure.correlationId}` : ''})
        </p>
      )}
      {reconciliation.notRun ? (
        <p className="aqsi-journal__note">Сверка ещё не запускалась.</p>
      ) : (
        <>
          <QueryState query={reconciliation.query} emptyText="Сверка не выполнялась" onRetry={actions.reload} />
          {reconciliation.query.status === 'ready' && (
            <>
              <p>Проверено: {reconciliation.checkedAtText}</p>
              {reconciliation.noSnapshot && (
                <p className="aqsi-journal__note" data-tone="warning" role="status">
                  <AlertTriangle size={16} aria-hidden="true" />
                  <span>
                    Полного снимка каталога кабинета нет — расхождения не считались. Это не значит, что всё совпало.
                  </span>
                </p>
              )}
              <Counts items={reconciliation.rows} label="Итог сверки" />
            </>
          )}
        </>
      )}
    </section>
  )
}
